/**
 * PII Validation Guard
 * 
 * Runtime PII detection and blocking for telemetry events.
 * Prevents accidental PII leaks before events are dispatched to PostHog/Sentry.
 * 
 * Fixes F005: High PII leak risk mitigation with runtime validation
 */

import { containsPII, sanitizeError } from './pii-redaction';

/**
 * Exception thrown when PII is detected in event properties
 */
export class PIIDetectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PIIDetectedError';
  }
}

/**
 * Validate event properties for PII before dispatch
 * 
 * Throws PIIDetectedError if PII patterns detected, preventing event from being sent.
 * This is a safety net to catch accidental PII inclusion at runtime.
 * 
 * Fixes F005: Runtime PII guard
 * 
 * @param properties - Event properties object
 * @throws {PIIDetectedError} if PII detected
 */
export function validateEventProperties(properties: Record<string, any>): void {
  if (!properties || typeof properties !== 'object') {
    return;
  }
  
  // Convert entire object to JSON for pattern matching
  const propsString = JSON.stringify(properties);
  
  // PII patterns to detect
  const piiPatterns = [
    { pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, type: 'Email' },
    { pattern: /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, type: 'Bearer Token' },
    { pattern: /\d+\.\d{4,}/g, type: 'Precise Coordinate' }, // >3 decimal places
  ];
  
  // Check for PII patterns
  for (const { pattern, type } of piiPatterns) {
    if (pattern.test(propsString)) {
      const error = `[Telemetry] PII detected in event properties (${type}) - event blocked for GDPR compliance`;
      
      // Log error in development
      if (import.meta.env.DEV) {
        console.error(error);
        console.error('Properties:', properties);
      }
      
      // Throw error to prevent event dispatch
      throw new PIIDetectedError(error);
    }
  }
}

/**
 * Safe wrapper for event tracking with PII validation
 * 
 * Validates properties before allowing the callback to execute.
 * Returns false if PII detected, true if event dispatched successfully.
 * 
 * @param properties - Event properties to validate
 * @param callback - Function to call if validation passes
 * @returns true if event dispatched, false if blocked
 */
export function safeTrackEvent(
  properties: Record<string, any> | undefined,
  callback: () => void
): boolean {
  try {
    if (properties) {
      validateEventProperties(properties);
    }
    callback();
    return true;
  } catch (error) {
    if (error instanceof PIIDetectedError) {
      // Log to console in development, silently block in production
      if (import.meta.env.DEV) {
        console.error('[Telemetry]', error.message);
      }
      
      // Optionally report PII detection to Sentry (without the PII itself)
      if (typeof window !== 'undefined' && (window as any).Sentry) {
        (window as any).Sentry.captureMessage(
          'PII detected in telemetry event properties',
          'warning'
        );
      }
      
      return false;
    }
    
    // Re-throw unexpected errors
    throw error;
  }
}

/**
 * Validate that coordinates are anonymized (not precise)
 * 
 * GPS coordinates should be rounded to ~10km grid (0.1° precision)
 * to comply with GDPR privacy requirements.
 * 
 * @param lat - Latitude
 * @param lon - Longitude
 * @throws {PIIDetectedError} if coordinates are too precise
 */
export function validateCoordinates(lat: number, lon: number): void {
  // Check if coordinates have more than 1 decimal place
  const latStr = lat.toString();
  const lonStr = lon.toString();
  
  const latDecimals = latStr.includes('.') ? latStr.split('.')[1].length : 0;
  const lonDecimals = lonStr.includes('.') ? lonStr.split('.')[1].length : 0;
  
  if (latDecimals > 1 || lonDecimals > 1) {
    throw new PIIDetectedError(
      '[Telemetry] Precise GPS coordinates detected - must be anonymized to 0.1° precision'
    );
  }
}

/**
 * Automated PII scanner for testing
 * 
 * Scans a collection of events and returns any that contain PII patterns.
 * Used in GDPR compliance tests.
 * 
 * @param events - Array of event objects to scan
 * @returns Array of events containing PII
 */
export function scanEventsForPII(events: Array<Record<string, any>>): Array<{
  event: Record<string, any>;
  piiType: string;
  match: string;
}> {
  const eventsWithPII: Array<{ event: Record<string, any>; piiType: string; match: string; }> = [];
  
  const piiPatterns = [
    { pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, type: 'Email' },
    { pattern: /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, type: 'Bearer Token' },
    { pattern: /\d+\.\d{4,}/g, type: 'Precise Coordinate' },
  ];
  
  for (const event of events) {
    const eventString = JSON.stringify(event);
    
    for (const { pattern, type } of piiPatterns) {
      const match = eventString.match(pattern);
      if (match) {
        eventsWithPII.push({
          event,
          piiType: type,
          match: match[0],
        });
      }
    }
  }
  
  return eventsWithPII;
}

/**
 * Validate that user ID is properly hashed
 * 
 * Checks that user ID follows expected hash format (16 hex characters)
 * instead of UUID or other identifiable format.
 * 
 * @param userId - User ID to validate
 * @throws {PIIDetectedError} if user ID is not hashed
 */
export function validateHashedUserId(userId: string): void {
  // Check if it looks like a UUID (not hashed)
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(userId)) {
    throw new PIIDetectedError(
      '[Telemetry] User ID appears to be unhashed UUID - must be hashed with HMAC-SHA256'
    );
  }
  
  // Check if it looks like an email (not hashed)
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  if (emailPattern.test(userId)) {
    throw new PIIDetectedError(
      '[Telemetry] User ID appears to be email address - must be hashed with HMAC-SHA256'
    );
  }
  
  // Optionally validate it's the expected format (16 hex chars)
  // This is lenient to allow 'anonymous' and other special values
  if (userId !== 'anonymous' && userId.length < 8) {
    console.warn('[Telemetry] User ID seems short - expected 16-char HMAC hash');
  }
}
